import { Button, Stack, Typography } from '@mui/material';
import dayjs, { type Dayjs } from 'dayjs';
import { useMemo, useState } from 'react';
import Alert from '/@/components/bayer/alert';
import { usePhenologyForProducts } from '/@/hooks/recommendation';
import { useUserLocations } from '/@/hooks/user';
import { _ } from '/@/locale';
import MuiDatePicker from '/@/mui/datepicker';

const PhenologyMiniCard = ({ productId, crop }: { productId: number; crop: string }) => {
    const [date, setDate] = useState<Dayjs | null>(dayjs());
    const [estimate, setEstimate] = useState<boolean>(false);

    const userLocations = useUserLocations({
        level: 2,
        pageSize: 100000,
    });
    const center = useMemo(() => {
        if (!userLocations.data?.results?.length) return null;

        const locations = userLocations.data.results;
        const validLocations = locations.filter(
            (loc: { bounds?: { north: number; south: number; east: number; west: number } }) => loc.bounds
        );

        if (validLocations.length === 0) return null;

        const { totalLat, totalLng } = validLocations.reduce(
            (acc: { totalLat: number; totalLng: number }, loc) => {
                const bounds = loc.bounds!;
                return {
                    totalLat: acc.totalLat + (bounds.north + bounds.south) / 2,
                    totalLng: acc.totalLng + (bounds.east + bounds.west) / 2,
                };
            },
            { totalLat: 0, totalLng: 0 }
        );

        return {
            lat: totalLat / validLocations.length,
            lng: totalLng / validLocations.length,
        };
    }, [userLocations.data]);

    const usePhenologyForProductQuery = usePhenologyForProducts(
        {
            crop,
            lat: center?.lat,
            lng: center?.lng,
            productsId: [productId],
            plantingDate: date?.format('YYYY-MM-DD'),
        },
        { enabled: !!center && estimate }
    );

    const gdusToMaturity = usePhenologyForProductQuery.data?.[productId]?.gdusTo?.maturity;
    const maturityDate = usePhenologyForProductQuery.data?.[productId]?.maturity;

    return (
        <Stack spacing={2} p={2} sx={{ border: (t) => `1px solid ${t.palette.divider}` }}>
            <Stack spacing={1}>
                <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                    {_('Madurez relativa ')}
                </Typography>
                <Typography variant='body2' color='primary.mui.inactive'>
                    {_('Seleccioná una fecha se siembra y estimá cuando es el periodo de madurez del cultivo.')}
                </Typography>
            </Stack>
            <Stack spacing={1} direction='row' alignItems='center'>
                <MuiDatePicker
                    label={_('Fecha de siembra')}
                    sx={{
                        width: '100%',
                    }}
                    value={date}
                    onChange={(e) => {
                        setDate(e);
                        setEstimate(false);
                    }}
                />
                <Button
                    variant='contained'
                    color='primary'
                    sx={{
                        height: '100%',
                    }}
                    onClick={() => {
                        setEstimate(true);
                    }}
                >
                    {_('Estimar')}
                </Button>
            </Stack>
            <Stack spacing={1}>
                {!gdusToMaturity && estimate && !usePhenologyForProductQuery.isValidating && (
                    <Alert severity='danger'>{_('No fue posible estimar la fecha de madurez')}</Alert>
                )}
                {maturityDate && (
                    <Stack>
                        <Typography variant='subtitle1' color='primary' fontWeight='bold'>
                            {_('Fecha estimada de madurez:')}
                        </Typography>
                        <Typography variant='subtitle1' color='primary'>
                            {maturityDate}
                        </Typography>
                    </Stack>
                )}
                {gdusToMaturity && (
                    <Typography variant='body2' color='primary.mui.disabled'>
                        {_('Se necesitan {{count}} GDUs para llegar a ese estadio.', {
                            count: gdusToMaturity.toFixed(0),
                        })}
                    </Typography>
                )}
            </Stack>
        </Stack>
    );
};

export default PhenologyMiniCard;
